import {
  VoiceWaveform,
  VoiceWaveformState,
} from "@/components/VoiceWaveform/VoiceWaveform";
import { ROUTES } from "@/constants/routes";
import { withSessionProvider } from "@/context/SessionContext";
import { UploadTarget } from "@/hooks/useFileRecordUpload";
import { micCaptureIsSupported } from "@/hooks/voice/micCapture";
import { useTranscriptPin } from "@/hooks/voice/useTranscriptPin";
import { useVoiceSession } from "@/hooks/voice/useVoiceSession";
import type { VoiceDraftV1 } from "@/hooks/voice/voiceDraftStorage";
import type { VoicePhase } from "@/hooks/voice/voiceSessionController";
import { isNativePlatform } from "@/native/platform";
import { VOICE_AUTOSTART_PARAM } from "@/native/quickActions";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@cohesive/ui-components/ui/drawer";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronLeft,
  Copy,
  FileText,
  Loader2,
  Mic,
  Pause,
  Play,
  Send,
  Square,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DestinationSheet } from "./DestinationSheet";
import { useVoiceDestinations } from "./hooks/useVoiceDestinations";
import { MobileScreen } from "./MobileScreen";
import { SavedFileNameSheet } from "./SavedFileNameSheet";

/**
 * Mobile voice v2: live streaming transcription with session chaining,
 * reconnect+replay, and crash-safe drafts (VoiceSessionController), ending in
 * a review step that sends the transcript as a chat message or saves it as a
 * .md file record — both into a workspace or canvas picked from a full-height
 * destination sheet. A file save lands immediately under a default name, then
 * offers an optional rename (SavedFileNameSheet) so exiting mid-name can never
 * lose the transcript. Mounted at `/m/voice` by MobileVoiceGate.
 */
function MobileVoicePageV2Inner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = searchParams.get("workspaceId") ?? undefined;

  const { controller, snapshot, draftStorage } = useVoiceSession();
  const { sendToWorkspace, sendToCanvas, saveMarkdown } =
    useVoiceDestinations();

  const [recoveredDraft, setRecoveredDraft] = useState<VoiceDraftV1 | null>(
    null,
  );
  const [checkedDraft, setCheckedDraft] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [confirm, setConfirm] = useState<
    null | "cancel" | "discard" | "discard-recovered"
  >(null);
  const [destinationFor, setDestinationFor] = useState<null | "chat" | "file">(
    null,
  );
  const [isDelivering, setIsDelivering] = useState(false);
  const [savedRecord, setSavedRecord] = useState<FileRecord | null>(null);

  const isSupported = useMemo(
    () => isNativePlatform() || micCaptureIsSupported(),
    [],
  );

  // A previous recording that never reached a destination — offer recovery.
  useEffect(() => {
    let stale = false;
    void draftStorage.load().then((draft) => {
      if (stale) return;
      setRecoveredDraft(draft);
      setCheckedDraft(true);
    });
    return () => {
      stale = true;
    };
  }, [draftStorage]);

  // Seed the editable review text when the session lands on review.
  const prevPhaseRef = useRef<VoicePhase>(snapshot.phase);
  useEffect(() => {
    if (snapshot.phase === "review" && prevPhaseRef.current !== "review") {
      setReviewText(controller.getReviewText());
    }
    prevPhaseRef.current = snapshot.phase;
  }, [snapshot.phase, controller]);

  const getLevels = useCallback(
    () => controller.getAudioLevels(),
    [controller],
  );

  const isRecordingActive =
    snapshot.phase === "connecting" ||
    snapshot.phase === "live" ||
    snapshot.phase === "rotating" ||
    snapshot.phase === "reconnecting" ||
    snapshot.phase === "paused" ||
    snapshot.phase === "finalizing";

  const start = useCallback(() => {
    void controller.start({ workspaceScopeId: workspaceId });
  }, [controller, workspaceId]);

  // A quick action / App Shortcut launch lands here with ?autostart=1: begin
  // recording the moment the draft check clears. The param is stripped as it
  // is consumed so back-nav or a reload never re-triggers, and it only starts
  // from a truly blank slate — an in-progress recording, review text, or an
  // unrecovered draft always wins over a new session (the ref latch guards the
  // Strict Mode double-fire on the same param instance).
  const autostartLatchRef = useRef(false);
  useEffect(() => {
    if (searchParams.get(VOICE_AUTOSTART_PARAM) !== "1") {
      autostartLatchRef.current = false;
      return;
    }
    if (!checkedDraft) return;
    const next = new URLSearchParams(searchParams);
    next.delete(VOICE_AUTOSTART_PARAM);
    setSearchParams(next, { replace: true });
    if (autostartLatchRef.current) return;
    autostartLatchRef.current = true;
    if (isSupported && !recoveredDraft && snapshot.phase === "idle") start();
  }, [
    searchParams,
    setSearchParams,
    checkedDraft,
    isSupported,
    recoveredDraft,
    snapshot.phase,
    start,
  ]);

  const goBack = useCallback(() => {
    navigate(
      workspaceId
        ? route(ROUTES.WORKSPACES.DETAIL, { workspaceId })
        : ROUTES.MOBILE.HOME,
    );
  }, [navigate, workspaceId]);

  const onBack = useCallback(() => {
    if (isRecordingActive && snapshot.fullText) {
      setConfirm("cancel");
    } else if (isRecordingActive) {
      void controller.cancel();
      goBack();
    } else {
      goBack();
    }
  }, [isRecordingActive, snapshot.fullText, controller, goBack]);

  const openChatSession = useCallback(
    (flowSessionId: string) => {
      navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId }));
    },
    [navigate],
  );

  const deliverChat = useCallback(
    async (target: UploadTarget) => {
      const content = reviewText.trim();
      if (!content || target.class === "flow-session") return;
      setIsDelivering(true);
      try {
        const { flowSessionId } =
          target.class === "canvas"
            ? await sendToCanvas(target.canvas_id, content)
            : await sendToWorkspace(target.workspace_id, content);
        await controller.markDelivered();
        openChatSession(flowSessionId);
      } catch {
        toast.error("Could not send the message. Your transcript is safe.");
      } finally {
        setIsDelivering(false);
      }
    },
    [reviewText, sendToCanvas, sendToWorkspace, controller, openChatSession],
  );

  const deliverFile = useCallback(
    async (target: UploadTarget) => {
      const content = reviewText.trim();
      if (!content || target.class === "flow-session") return;
      setIsDelivering(true);
      try {
        const record = await saveMarkdown(target, content);
        await controller.markDelivered();
        // The file is durably saved under its default name — offer an
        // optional rename before leaving; every exit path keeps the file.
        setSavedRecord(record);
      } catch {
        toast.error("Could not save the file. Your transcript is safe.");
      } finally {
        setIsDelivering(false);
      }
    },
    [reviewText, saveMarkdown, controller],
  );

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(reviewText);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }, [reviewText]);

  const waveformState: VoiceWaveformState =
    snapshot.phase === "live" || snapshot.phase === "rotating"
      ? "live"
      : snapshot.phase === "reconnecting"
        ? "reconnecting"
        : snapshot.phase === "paused"
          ? "paused"
          : snapshot.phase === "connecting"
            ? "connecting"
            : "idle";

  return (
    <MobileScreen>
      <div className="flex min-h-0 w-full flex-1 flex-col py-3">
        <div className="flex items-center gap-2 pb-2">
          <button
            type="button"
            onClick={onBack}
            className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Voice</h1>
          <div className="ml-auto">
            <StatusChip snapshot={snapshot} />
          </div>
        </div>

        {!isSupported ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="max-w-xs text-center text-sm text-muted-foreground">
              Voice recording isn&apos;t supported on this device.
            </p>
          </div>
        ) : snapshot.phase === "error" ? (
          <ErrorState
            message={snapshot.error}
            onRetry={() => controller.acknowledgeError()}
          />
        ) : snapshot.phase === "review" ? (
          <ReviewView
            reviewText={reviewText}
            onChange={(text) => {
              setReviewText(text);
              controller.updateReviewText(text);
            }}
            elapsedSec={snapshot.elapsedSec}
            isDelivering={isDelivering}
            onSend={() => setDestinationFor("chat")}
            onSave={() => setDestinationFor("file")}
            onCopy={copy}
            onDiscard={() => setConfirm("discard")}
          />
        ) : isRecordingActive ? (
          <RecordingView
            snapshot={snapshot}
            getLevels={getLevels}
            waveformState={waveformState}
            onPause={() => controller.pause()}
            onResume={() => void controller.resume()}
            onStop={() => controller.stop()}
            onCancel={() =>
              snapshot.fullText
                ? setConfirm("cancel")
                : void controller.cancel()
            }
          />
        ) : !checkedDraft ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : recoveredDraft ? (
          <RecoveryView
            draft={recoveredDraft}
            onReview={() => {
              controller.restoreDraft(recoveredDraft);
              setRecoveredDraft(null);
            }}
            onDiscard={() => setConfirm("discard-recovered")}
          />
        ) : (
          <IdleView onStart={start} />
        )}
      </div>

      <DestinationSheet
        open={destinationFor !== null}
        onOpenChange={(open) => {
          if (!open) setDestinationFor(null);
        }}
        title={destinationFor === "file" ? "Save to…" : "Send to…"}
        workspaceActionLabel={
          destinationFor === "file"
            ? "Save in this workspace"
            : "Send to this workspace"
        }
        showRecents
        initialWorkspaceId={undefined}
        onSelect={(target) => {
          if (destinationFor === "file") {
            void deliverFile(target);
          } else {
            void deliverChat(target);
          }
        }}
      />

      <SavedFileNameSheet
        record={savedRecord}
        onDone={() => {
          setSavedRecord(null);
          goBack();
        }}
      />

      <ConfirmSheet
        open={confirm !== null}
        title={
          confirm === "cancel" ? "Discard recording?" : "Discard transcript?"
        }
        body="The transcript will be permanently deleted."
        confirmLabel="Discard"
        onConfirm={() => {
          const which = confirm;
          setConfirm(null);
          if (which === "cancel") {
            void controller.cancel().then(goBack);
          } else if (which === "discard") {
            void controller.discard();
          } else if (which === "discard-recovered") {
            void draftStorage.clear();
            setRecoveredDraft(null);
          }
        }}
        onClose={() => setConfirm(null)}
      />
    </MobileScreen>
  );
}

function formatElapsed(totalSec: number): string {
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const mmss = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

function StatusChip({
  snapshot,
}: {
  snapshot: ReturnType<typeof useVoiceSession>["snapshot"];
}) {
  let dotClass = "bg-muted-foreground";
  let label: string | null = null;
  switch (snapshot.phase) {
    case "live":
    case "rotating":
      dotClass = "bg-lc-blocked animate-pulse";
      label = "Recording";
      break;
    case "connecting":
      label = "Connecting…";
      break;
    case "reconnecting":
      dotClass = "bg-lc-warning";
      label =
        snapshot.bufferSecondsRemaining !== null &&
        snapshot.bufferSecondsRemaining < 120
          ? `Reconnecting… ${snapshot.bufferSecondsRemaining}s buffer`
          : "Reconnecting…";
      break;
    case "paused":
      dotClass = "bg-lc-waiting";
      label =
        snapshot.pauseReason === "buffer_exhausted"
          ? snapshot.bufferSecondsRemaining === null
            ? "Reconnected — resume when ready"
            : "Paused — connection lost"
          : snapshot.pauseReason === "interruption"
            ? "Paused — interrupted"
            : "Paused";
      break;
    case "finalizing":
      label = "Finishing…";
      break;
    default:
      return null;
  }
  return (
    <span className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
      <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} />
      {label}
    </span>
  );
}

function IdleView({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 pb-safe">
      <button
        type="button"
        onClick={onStart}
        className="flex h-28 w-28 items-center justify-center rounded-full bg-rust text-white shadow-lg transition-all active:scale-95"
        aria-label="Record"
      >
        <Mic className="h-10 w-10" />
      </button>
      <p className="max-w-xs text-center text-sm text-muted-foreground">
        Tap to dictate. The transcript streams live — send it as a chat or save
        it as a doc when you&apos;re done.
      </p>
    </div>
  );
}

function RecordingView({
  snapshot,
  getLevels,
  waveformState,
  onPause,
  onResume,
  onStop,
  onCancel,
}: {
  snapshot: ReturnType<typeof useVoiceSession>["snapshot"];
  getLevels: () => { level: number; history: readonly number[] };
  waveformState: VoiceWaveformState;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onCancel: () => void;
}) {
  const { ref, onScroll, scrolled } = useTranscriptPin(snapshot.fullText);
  const isPaused = snapshot.phase === "paused";
  const isFinalizing = snapshot.phase === "finalizing";
  const canResumeFromExhaustion =
    isPaused &&
    snapshot.pauseReason === "buffer_exhausted" &&
    snapshot.bufferSecondsRemaining === null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 pb-safe pt-2">
      <div className="text-center font-mono text-4xl font-light tabular-nums">
        {formatElapsed(snapshot.elapsedSec)}
      </div>

      <VoiceWaveform
        getLevels={getLevels}
        state={waveformState}
        className="h-16"
      />

      <div className="relative min-h-0 flex-1">
        {snapshot.fullText ? (
          <>
            <div
              ref={ref}
              onScroll={onScroll}
              className="no-scrollbar h-full overflow-y-auto rounded-xl border bg-background px-4 py-3 text-base leading-relaxed"
            >
              {snapshot.fullText}
            </div>
            {scrolled && (
              <div className="pointer-events-none absolute inset-x-0 top-0 h-8 rounded-t-xl bg-gradient-to-b from-background to-transparent" />
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed">
            <p className="px-6 text-center text-sm text-muted-foreground">
              {snapshot.phase === "connecting"
                ? "Connecting…"
                : "Start talking — your words appear here as you speak."}
            </p>
          </div>
        )}
      </div>

      {canResumeFromExhaustion && (
        <p className="text-center text-xs text-muted-foreground">
          The connection is back. Audio during the outage wasn&apos;t recorded —
          resume when you&apos;re ready.
        </p>
      )}

      <div className="flex items-center justify-center gap-8">
        <button
          type="button"
          onClick={isPaused ? onResume : onPause}
          disabled={isFinalizing}
          className="flex h-14 w-14 items-center justify-center rounded-full border text-foreground active:bg-accent disabled:opacity-40"
          aria-label={isPaused ? "Resume" : "Pause"}
        >
          {isPaused ? (
            <Play className="h-6 w-6" />
          ) : (
            <Pause className="h-6 w-6" />
          )}
        </button>
        <button
          type="button"
          onClick={onStop}
          disabled={isFinalizing}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-rust text-white shadow-lg transition-all active:scale-95 disabled:opacity-60"
          aria-label="Stop and review"
        >
          {isFinalizing ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            <Square className="h-7 w-7 fill-current" />
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isFinalizing}
          className="flex h-14 w-14 items-center justify-center rounded-full text-muted-foreground active:bg-accent disabled:opacity-40"
          aria-label="Discard recording"
        >
          <Trash2 className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}

function ReviewView({
  reviewText,
  onChange,
  elapsedSec,
  isDelivering,
  onSend,
  onSave,
  onCopy,
  onDiscard,
}: {
  reviewText: string;
  onChange: (text: string) => void;
  elapsedSec: number;
  isDelivering: boolean;
  onSend: () => void;
  onSave: () => void;
  onCopy: () => void;
  onDiscard: () => void;
}) {
  const wordCount = reviewText.trim()
    ? reviewText.trim().split(/\s+/).length
    : 0;
  const secondaryBtn =
    "flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium text-foreground active:bg-accent disabled:opacity-50";
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3 pb-safe pt-2">
      <textarea
        value={reviewText}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[160px] flex-1 resize-none rounded-xl border bg-background p-4 text-base outline-none focus:ring-2 focus:ring-rust"
        placeholder="Your dictation…"
      />
      <p className="px-1 text-xs text-muted-foreground">
        {formatElapsed(elapsedSec)} · {wordCount}{" "}
        {wordCount === 1 ? "word" : "words"}
      </p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onSend}
          disabled={isDelivering || !reviewText.trim()}
          className="flex items-center justify-center gap-2 rounded-xl bg-rust px-4 py-3.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {isDelivering ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Send as message
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={isDelivering || !reviewText.trim()}
          className={secondaryBtn}
        >
          <FileText className="h-4 w-4" /> Save as file
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={!reviewText.trim()}
            className={cn(secondaryBtn, "flex-1")}
          >
            <Copy className="h-4 w-4" /> Copy
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={isDelivering}
            className={cn(secondaryBtn, "flex-1 text-muted-foreground")}
          >
            <Trash2 className="h-4 w-4" /> Discard
          </button>
        </div>
      </div>
    </div>
  );
}

function RecoveryView({
  draft,
  onReview,
  onDiscard,
}: {
  draft: VoiceDraftV1;
  onReview: () => void;
  onDiscard: () => void;
}) {
  const preview =
    draft.reviewText ??
    [...draft.committedSegments.map((segment) => segment.text), draft.liveText]
      .filter(Boolean)
      .join(" ");
  const interrupted = draft.status === "recording";
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 pb-safe">
      <div className="w-full max-w-md rounded-2xl border p-4">
        <p className="text-sm font-medium">
          {interrupted ? "Recovered voice note" : "Unsent voice note"}
        </p>
        <p className="pt-0.5 text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(draft.updatedAt), { addSuffix: true })}
          {interrupted && " · recording was interrupted"}
        </p>
        <p className="line-clamp-3 pt-2 text-sm text-muted-foreground">
          {preview || "(empty)"}
        </p>
        <div className="flex items-center gap-2 pt-4">
          <button
            type="button"
            onClick={onDiscard}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium text-muted-foreground active:bg-accent"
          >
            <Trash2 className="h-4 w-4" /> Discard
          </button>
          <button
            type="button"
            onClick={onReview}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rust px-4 py-3 text-sm font-medium text-white"
          >
            Review
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 pb-safe">
      <p className="max-w-xs text-center text-sm text-muted-foreground">
        {message ?? "Something went wrong."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-xl border px-4 py-2.5 text-sm font-medium active:bg-accent"
      >
        Try again
      </button>
    </div>
  );
}

function ConfirmSheet({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-3 px-4 pb-safe">
          <p className="text-sm text-muted-foreground">{body}</p>
          <div className="flex items-center gap-2 pb-2">
            <button
              type="button"
              onClick={onClose}
              className="flex flex-1 items-center justify-center rounded-xl border px-4 py-3 text-sm font-medium active:bg-accent"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex flex-1 items-center justify-center rounded-xl bg-lc-blocked px-4 py-3 text-sm font-medium text-white"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export const MobileVoicePageV2 = withSessionProvider(MobileVoicePageV2Inner);
export default MobileVoicePageV2;
